import { CoreImageView } from '@core/widgets';
import { useApp, useBehavior, useCache, useList, useSubscribeOnWsUpdate, useTriggerAction, useUpdateWidgetValue } from '@libs/hooks';
import { ActionActionTypeEnum, AssetsSvgMap, TItem, TWAction, TWidget, TWidgetProps } from '@libs/types';
import { ConditionalRender, eventBus } from '@libs/utils';
import clsx from 'clsx';
import React, { Activity, useCallback, useEffect, useEffectEvent, useRef, useState } from 'react';
import { getHref } from '../Image/imageUtils';
import { SpinnerCircle } from '../UI/Spinner/SpinnerCircle';
import { TConsumerItem, TConsumerListData, TListDataMessages } from '../../types/data';
import { TListOption } from '../../types/options';
import { getInlineStyles } from '../../utils/styles';
import { TextCommon } from '../UI/Text/TextCommon';
import TransactionsListItems from './TransactionsListItems';
import styles from './styles/styles.module.css';
import { convertTransactionItems, mergeArrays } from './utils/transactionUtils';

export const TransactionsList: React.FC<TWidgetProps<TConsumerListData, TListOption>> = ({ widget }) => {
    const triggerRef = useRef(null);
    const fetchingData = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { getCache, putCache } = useCache(widget);
    const { isDisabled, isHidden } = useBehavior(widget);

    const actions = widget.actions;
    const state = widget.options?.state ?? 'FULL';
    const margin = widget.options?.margin;
    const emptyStateAlign = widget.options?.emptyStateAlign ?? 'center';
    const emptyPlaceholderIcon = widget.options?.emptyPlaceholderIcon ?? AssetsSvgMap.ic_dotted_list;
    const data = widget.data ?? {};
    const paging = data.settings?.paging ?? {};
    const messages = data.messages ?? ({} as TListDataMessages);

    const [items, setItems] = useState<TConsumerItem[]>(convertTransactionItems(data?.items ?? []));
    const { updateWidgetValue } = useUpdateWidgetValue(widget);
    const { triggerAction, triggerActionsByType } = useTriggerAction(widget);
    const { setModal, setPopup, setBottomSheet } = useApp();
    const { isVisible } = useList(widget, { triggerActionsByType, setModal, setPopup, setBottomSheet }, triggerRef);
    const actionEndOfList = actions?.find((a) => a?.actionType === ActionActionTypeEnum.onEndOfList);
    const hasItems = items && Array.isArray(items) && items.length > 0;
    const [showSpinner, toggleSpinner] = useState(true !== paging?.theEnd && !!actionEndOfList && hasItems);
    const onRefreshAction = actions?.find((a) => a?.actionType === ActionActionTypeEnum.onRefresh);

    useSubscribeOnWsUpdate(onRefreshAction);

    const mergeItemsAndUpdateCache = useCallback(
        (currentItems: Array<TConsumerItem>, newItems: Array<TConsumerItem>) => {
            const mergeItems = mergeArrays(convertTransactionItems(currentItems), convertTransactionItems(newItems));
            if (mergeItems.isHasChanges) {
                setItems(mergeItems.items);
                putCache({
                    ...widget?.data,
                    items: mergeItems.items,
                }).catch((e) => console.warn('Cannot update cache...', e));
            }
        },
        [putCache, widget.data],
    );

    useEffect(() => {
        getCache().then((cacheData) => {
            if (cacheData) {
                setItems(convertTransactionItems(cacheData?.items ?? []));
            } else {
                mergeItemsAndUpdateCache([], data.items);
            }
        });
    }, [data.items, getCache, mergeItemsAndUpdateCache]);

    useEffect(() => {
        eventBus.subscribeWS(widget?.internalId || '', (w) => {
            if (!w || !w.data) return;
            if (fetchingData.current) return;

            const cW = w as TWidget<TConsumerListData, TListOption>;
            const newItems = cW?.data?.items;
            if (Array.isArray(newItems) && newItems.length > 0) {
                mergeItemsAndUpdateCache(items, newItems);
            }
        });
        return () => {
            eventBus.unsubscribeWsSpecificWidget(widget.internalId);
        };
    }, [items, mergeItemsAndUpdateCache, widget.internalId]);

    const onTriggerAction = useEffectEvent((action: TWAction) => {
        return triggerAction(action);
    });

    useEffect(() => {
        let timeoutId = timeoutRef?.current;

        async function startFetching() {
            if (showSpinner && actionEndOfList && isVisible && !fetchingData.current) {
                fetchingData.current = true;
                const result = await onTriggerAction(actionEndOfList);
                if (result) {
                    const responseData = result.data;
                    if (Array.isArray(responseData.items) && responseData.items.length > 0) {
                        mergeItemsAndUpdateCache(items, responseData.items);
                    }
                    toggleSpinner(true !== responseData.settings?.paging?.theEnd);
                } else {
                    toggleSpinner(false);
                }
            }
        }

        startFetching()
            .catch((e) => console.warn(e))
            .finally(() => {
                timeoutId = setTimeout(() => (fetchingData.current = false), 500);
            });
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [showSpinner, items, isVisible, actionEndOfList, mergeItemsAndUpdateCache]);

    const handleClick = (_uid: string, item: TItem) => {
        if (isDisabled) return;
        const value = item?.value;
        updateWidgetValue(value);
        triggerActionsByType(ActionActionTypeEnum.onItemSelected, { value }).then();
    };

    const emptyTextAlign = emptyStateAlign === 'left' ? 'start' : emptyStateAlign === 'right' ? 'end' : 'center';

    return (
        <Activity mode={isHidden ? 'hidden' : 'visible'}>
            <div
                data-test={widget.uid}
                aria-disabled={isDisabled}
                className={clsx(
                    !hasItems && styles.emptyListContainer,
                    !hasItems && emptyStateAlign === 'left' && styles.emptyListContainerLeft,
                    !hasItems && emptyStateAlign === 'right' && styles.emptyListContainerRight,
                    isDisabled && styles.disabled,
                )}
                style={getInlineStyles({ margin: margin })}
            >
                <ConditionalRender condition={!hasItems}>
                    <div
                        className={clsx(
                            styles.emptyContainer,
                            emptyStateAlign === 'left' && styles.emptyContainerLeft,
                            emptyStateAlign === 'right' && styles.emptyContainerRight,
                        )}
                    >
                        <CoreImageView
                            src={getHref(emptyPlaceholderIcon)}
                            defaultIcon={getHref(AssetsSvgMap.ic_dotted_list)}
                            styleProps={{
                                iconColor: `var(--color-grey_72_lc)`,
                            }}
                        />
                        <TextCommon
                            isWidget={false}
                            content={messages.emptyPlaceholder}
                            options={{
                                variant: 'secondary',
                                textAlign: emptyTextAlign,
                                textColor: 'grey_72_lc',
                                margin: { top: 16 },
                            }}
                        />
                    </div>
                </ConditionalRender>
                <ConditionalRender condition={hasItems}>
                    <TransactionsListItems wid={widget.uid} handleClick={handleClick} items={items} messages={messages} state={state} />
                    <ConditionalRender condition={showSpinner}>
                        <div className={styles.spinnerContainer} ref={triggerRef}>
                            <SpinnerCircle size={'32px'} />
                        </div>
                    </ConditionalRender>
                </ConditionalRender>
            </div>
        </Activity>
    );
};
